/**
 * ResetPasswordForm Component
 * 
 * Renders the password reset request form where users enter their email
 * to receive a password reset link. Includes CAPTCHA verification.
 * 
 * @param {Object} props - Component props
 * @param {Object} props.credentials - Object with email field
 * @param {Function} props.setCredentials - State setter for credentials
 * @param {Function} props.handleChangePwd - Callback to submit password reset request
 * @param {string} props.response - Alert message to display
 * @param {string} props.captchaToken - CAPTCHA verification token
 * @param {Function} props.setCaptchaToken - Sets CAPTCHA token
 * @returns {JSX.Element} Password reset request form\n */
import React, { useState } from 'react'
import './reset-password-form.css'
import { Card, Button, Form, Alert } from 'react-bootstrap'
import HeaderLogo from '../../../assets/navbar/roiquant-600x200px-white-01.png';
import { Turnstile } from '@marsidev/react-turnstile';

/**
 * The `ResetPasswordForm` function is a React component that renders a form for resetting a user's
 * password, including email input, captcha verification, and a reset password button.
 * @param props - Props are properties that are passed to a React component from its parent component.
 * In the `ResetPasswordForm` component, the `props` parameter is an object that contains any data or
 * functions passed down from the parent component. These props can be accessed within the component to
 * render dynamic content or trigger actions
 * @returns The `ResetPasswordForm` component is being returned. It contains JSX elements for a
 * password reset form with input fields for email, a Turnstile captcha component, a button to reset
 * the password, and a link to log in.
 */

function ResetPasswordForm(props) {

  // Track CAPTCHA completion status
  const [captchaCompleted, setCaptchaCompleted] = useState(false);

  return (

    <div id="changepasswordcontent">
      <div className="changepasswordtop">
        <a href="https://www.roiquant.com/" target="_blank" rel="noopener noreferrer">
          <img src={HeaderLogo} alt="roiquant" width="180px" height="60px" className="roiquantlogo" />
        </a>
      </div>

      <div>{props.response}</div>

      <div id="changepasswordform">
        <Card className="changepasswordcard">
          <div className="changepasswordheader">

            <h3>Reset your password</h3>
          </div>

          <div className="createaccnote">
            <p>We'll email you to confirm your password reset.</p>
          </div>

          <Form>
            <Form.Group className="inputgroup" controlId="formBasicEmail">
              {/* Email input field for password reset */}
              <Form.Control className="inputbox" type="email" name="email" placeholder="Enter email" value={props.credentials.email}
                onChange={(e) => { props.setCredentials(Object.assign({}, props.credentials, { email: e.target.value })) }}
              >
              </Form.Control>
            </Form.Group>

            {/* CAPTCHA verification to prevent bot submissions */}
            <Turnstile
              siteKey={process.env.REACT_APP_TURNSTILE}
              onSuccess={(token) => {
                setCaptchaCompleted(true);
                props.setCaptchaToken(token);
              }}
              onExpire={() => {
                setCaptchaCompleted(false);
                props.setCaptchaToken("");
              }}
              onError={() => {
                setCaptchaCompleted(false);
                props.setCaptchaToken("");
              }}
              options={{
                size: 'flexible', // or 'normal', or 'compact'
                theme: 'light',
              }}
            />

            {/* Submit button - only enabled after CAPTCHA completion */}
            <div style={{ textAlign: "center" }}>
              {captchaCompleted ?
                (<Button type="submit" className="changepasswordbtn glintanimation" onClick={(event) => { event.preventDefault(); props.handleChangePwd() }}>
                  <span>Reset password</span>
                </Button>) : (<Button type="submit" className="changepasswordbtn glintanimation" disabled>
                  <span>Reset password</span>
                </Button>)
              }
            </div>

          </Form>

          <div className="loginnote">
            <span>Got your password? <a href="/login">Log in</a></span>
          </div>

        </Card>
      </div>
    </div>
  )
}

export default ResetPasswordForm;
